import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { X, Check } from 'lucide-react';
import './RadiusMapModal.css';
import { getMapboxToken } from '../../../../utils/utils';

mapboxgl.accessToken = getMapboxToken();

const STORE_COORDS = [51.427533, 25.200859]; // [lon, lat]

// Generate a GeoJSON circle polygon from center + radius in km.
function createCircleGeoJSON(center, radiusKm, steps = 64) {
  const coords = [];
  const earthRadius = 6371; // km
  const lat = center[1] * (Math.PI / 180);

  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    // dLat drives north/south, dLon drives east/west
    const dLat = (radiusKm / earthRadius) * Math.sin(angle);
    const dLon = (radiusKm / earthRadius) * Math.cos(angle) / Math.cos(lat);
    coords.push([
      center[0] + dLon * (180 / Math.PI),
      center[1] + dLat * (180 / Math.PI)
    ]);
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coords]
    }
  };
}

/** Bounding box that tightly wraps the circle — used only for initial fit. */
function getCircleBounds(center, radiusKm) {
  const latOffset = radiusKm / 111.32;
  const lngOffset = radiusKm / (111.32 * Math.cos(center[1] * (Math.PI / 180)));
  return [
    [center[0] - lngOffset, center[1] - latOffset],
    [center[0] + lngOffset, center[1] + latOffset]
  ];
}

const RadiusMapModal = ({ isOpen, onClose, currentRange, onConfirm }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const mapReady = useRef(false);                 // true once 'load' fires
  const initialFitDone = useRef(false);           // fit only once per open
  const [sliderValue, setSliderValue] = useState(currentRange || 10);

  // Sync slider when modal opens with a (possibly new) currentRange
  useEffect(() => {
    if (isOpen) {
      setSliderValue(currentRange || 10);
      initialFitDone.current = false;             // allow one fit on next open
    }
  }, [isOpen, currentRange]);

  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      if (!mapContainer.current || map.current) return;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: STORE_COORDS,
        zoom: 12,
        interactive: true
      });

      map.current.addControl(
        new mapboxgl.NavigationControl({ showCompass: false }),
        'top-right'
      );

      map.current.on('load', () => {
        mapReady.current = true;

        // Store marker
        const el = document.createElement('div');
        el.className = 'store-marker';
        el.innerHTML = '🏪';
        Object.assign(el.style, {
          fontSize: '22px',
          background: 'white',
          borderRadius: '50%',
          padding: '4px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px'
        });

        new mapboxgl.Marker({ element: el })
          .setLngLat(STORE_COORDS)
          .addTo(map.current);

        // Circle source + layers
        map.current.addSource('radius-circle', {
          type: 'geojson',
          data: createCircleGeoJSON(STORE_COORDS, sliderValue)
        });

        map.current.addLayer({
          id: 'radius-circle-fill',
          type: 'fill',
          source: 'radius-circle',
          paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.12 }
        });

        map.current.addLayer({
          id: 'radius-circle-border',
          type: 'line',
          source: 'radius-circle',
          paint: { 'line-color': '#3b82f6', 'line-width': 2.5, 'line-opacity': 0.8 }
        });

        // Initial fit — only once
        if (!initialFitDone.current) {
          map.current.fitBounds(
            getCircleBounds(STORE_COORDS, sliderValue),
            { padding: 60, duration: 600 }
          );
          initialFitDone.current = true;
        }
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      mapReady.current = false;
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [isOpen]); 

  useEffect(() => {
    if (!map.current || !mapReady.current) return;

    const source = map.current.getSource('radius-circle');
    if (source) {
      source.setData(createCircleGeoJSON(STORE_COORDS, sliderValue));
    }
  }, [sliderValue]);

  const handleConfirm = () => {
    onConfirm(sliderValue);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="radius-modal-overlay" onClick={onClose}>
      <div className="radius-modal" onClick={(e) => e.stopPropagation()}>
        <div className="radius-modal-header">
          <h3>Express Delivery Radius</h3>
          <button className="radius-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="radius-modal-map-container" ref={mapContainer} />

        <div className="radius-modal-controls">
          <div className="radius-slider-row">
            <span className="radius-slider-label">Delivery Radius</span>
            <span className="radius-slider-value">{sliderValue} km</span>
          </div>
          <input
            type="range"
            className="radius-slider"
            min="1"
            max="30"
            step="1"
            value={sliderValue}
            onChange={(e) => setSliderValue(Number(e.target.value))}
          />
          <div className="radius-slider-ticks">
            <span>1 km</span>
            <span>15 km</span>
            <span>30 km</span>
          </div>
        </div>

        <div className="radius-modal-footer">
          <button className="radius-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="radius-modal-confirm" onClick={handleConfirm}>
            <Check size={16} />
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default RadiusMapModal;
